import React, { useState, useRef, useEffect, useContext } from 'react';
import { AuthContext } from './contexts/AuthContext';
import { ChatContext } from './contexts/ChatContext';
import ChartRenderer from './ChartRenderer';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { PlusCircle, ClockHistory, Trash, Stars } from 'react-bootstrap-icons';
import TextoConFotos from './ia/TextoConFotos';

const styles = {
  container: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    zIndex: 1000,
    fontFamily: 'Arial, sans-serif'
  },
  button: {
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '60px',
    height: '60px',
    cursor: 'pointer',
    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    fontSize: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  chatWindow: {
    width: '350px',
    height: '500px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    marginBottom: '15px'
  },
  header: {
    backgroundColor: '#007bff',
    color: 'white',
    padding: '10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  messagesArea: {
    flex: 1,
    padding: '10px',
    overflowY: 'auto',
    backgroundColor: '#f9f9f9'
  },
  inputArea: {
    padding: '10px',
    borderTop: '1px solid #eee',
    display: 'flex'
  },
  input: {
    flex: 1,
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    marginRight: '5px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    resize: 'none',
    minHeight: '38px',
    maxHeight: '120px',
    overflowY: 'auto'
  },
  msgUser: {
    backgroundColor: '#e3f2fd',
    padding: '8px',
    borderRadius: '10px 10px 0 10px',
    marginBottom: '8px',
    alignSelf: 'flex-end',
    maxWidth: '80%',
    marginLeft: 'auto'
  },
  msgBot: {
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    padding: '8px',
    borderRadius: '10px 10px 10px 0',
    marginBottom: '8px',
    maxWidth: '95%'
  },
  typingDots: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 0'
  },
  typingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#2563eb',
    animation: 'chatPulse 1.4s infinite ease-in-out both'
  },
  headerBtn: {
    background: 'transparent',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    padding: '2px 4px'
  },
  convItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
    cursor: 'pointer',
    fontSize: '13px'
  }
};

// Keyframes de la animación de "pensando..." (tres puntos pulsantes).
if (typeof document !== 'undefined' && !document.getElementById('chat-typing-keyframes')) {
  const s = document.createElement('style');
  s.id = 'chat-typing-keyframes';
  s.textContent = `
    @keyframes chatPulse {
      0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
      40% { transform: scale(1); opacity: 1; }
    }
  `;
  document.head.appendChild(s);
}

const ChatWidget = () => {
  const { user, isAuth } = useContext(AuthContext);
  const {
    isOpen,
    setIsOpen,
    conversaciones,
    activeConv,
    messages,
    isLoading,
    loadingConv,
    cargarConversaciones,
    seleccionarConversacion,
    nuevaConversacion,
    eliminarConversacion,
    enviar
  } = useContext(ChatContext);

  const [input, setInput] = useState('');
  const [view, setView] = useState('chat'); // 'chat' | 'history'
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
      if (isOpen && view === 'chat') {
          scrollToBottom();
      }
  }, [messages, isOpen, view]);

  const abrirHistorial = () => {
    setView('history');
    cargarConversaciones();
  };

  useEffect(() => {
    if (!isLoading && isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading, isOpen]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
    }
  };

  // Función para limpiar datos: eliminar _id y ObjectIds vacíos
  const cleanDataForExport = (data) => {
    if (!Array.isArray(data)) return data;
    return data.map(row => {
      const cleanRow = {};
      for (const [key, value] of Object.entries(row)) {
        if (key === '_id') continue;
        if (value === null || value === undefined || value === '') continue;
        if (Array.isArray(value) && value.length === 0) continue;
        if (typeof value === 'object' && !Array.isArray(value)) {
          const keys = Object.keys(value);
          if (keys.includes('_bsontype') || keys.includes('$oid')) continue;
        }
        cleanRow[key] = value;
      }
      return cleanRow;
    });
  };

  const downloadExcel = (data, filename = 'datos') => {
    try {
      let worksheetData = Array.isArray(data) ? data : (data && typeof data === 'object' ? [data] : []);
      const cleanedData = cleanDataForExport(worksheetData);
      if (!cleanedData || cleanedData.length === 0) {
        alert('No hay datos disponibles para exportar');
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(cleanedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
      const timestamp = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `${filename}_${timestamp}.xlsx`);
    } catch (error) {
      console.error('Error generando Excel:', error);
      alert('Error al generar el archivo Excel');
    }
  };

  const downloadPDF = async (messageContent, chartData = null, chartElementId = null, filename = 'reporte') => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      let yPosition = 20;

      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(`Reporte ${import.meta.env.VITE_NOMBRE_INSTITUCION || 'Asistente IA'}`, 14, yPosition);
      yPosition += 10;

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Fecha: ${new Date().toLocaleString()}`, 14, yPosition);
      yPosition += 10;

      if (messageContent) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Respuesta:', 14, yPosition);
        yPosition += 8;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        const splitText = doc.splitTextToSize(messageContent, pageWidth - 28);
        doc.text(splitText, 14, yPosition);
        yPosition += (splitText.length * 5) + 10;
      }

      if (chartElementId) {
        const chartElement = document.getElementById(chartElementId);
        if (chartElement) {
          try {
            const canvas = await html2canvas(chartElement, { backgroundColor: '#ffffff', scale: 2 });
            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - 28;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            if (yPosition + imgHeight > doc.internal.pageSize.height - 20) {
              doc.addPage();
              yPosition = 20;
            }
            doc.addImage(imgData, 'PNG', 14, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 10;
          } catch (error) {
            console.warn('No se pudo capturar la gráfica:', error);
          }
        }
      }

      if (!chartElementId && chartData && chartData.data && Array.isArray(chartData.data) && chartData.data.length > 0) {
        const cleanedData = cleanDataForExport(chartData.data.slice(0, 50));
        if (cleanedData && cleanedData.length > 0) {
          const headers = Object.keys(cleanedData[0]);
          const rows = cleanedData.map(row => headers.map(header => row[header] || ''));
          doc.autoTable({
            head: [headers],
            body: rows,
            startY: yPosition,
            margin: { left: 14, right: 14 },
            styles: { fontSize: 8 },
            headStyles: { fillColor: [0, 123, 255] }
          });
        }
      }

      const timestamp = new Date().toISOString().split('T')[0];
      doc.save(`${filename}_${timestamp}.pdf`);
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el archivo PDF');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const currentInput = input;
    setInput('');
    await enviar(currentInput);
  };

  if (!isAuth || !user || !user.menu_ia) {
    return null;
  }

  return (
    <div style={styles.container}>
      {isOpen && (
        <div style={styles.chatWindow}>
          <div style={styles.header}>
            <strong>{view === 'history' ? 'Historial' : `Asistente ${import.meta.env.VITE_NOMBRE_INSTITUCION || ''}`}</strong>
            <div style={{display: 'flex', gap: '4px'}}>
              {view === 'chat' ? (
                <>
                  <button onClick={() => { nuevaConversacion(); setView('chat'); }} title="Nueva conversación" style={styles.headerBtn}>
                    <PlusCircle size={16} />
                  </button>
                  <button onClick={abrirHistorial} title="Historial" style={styles.headerBtn}>
                    <ClockHistory size={16} />
                  </button>
                  {activeConv && (
                    <button onClick={() => eliminarConversacion(activeConv._id)} title="Eliminar conversación" style={styles.headerBtn}>
                      <Trash size={16} />
                    </button>
                  )}
                </>
              ) : (
                <button onClick={() => setView('chat')} title="Volver al chat" style={styles.headerBtn}>
                  ← Volver
                </button>
              )}
              <button onClick={() => setIsOpen(false)} style={styles.headerBtn}>✕</button>
            </div>
          </div>

          {view === 'history' ? (
            <div style={{...styles.messagesArea, padding: 0}}>
              {conversaciones.length === 0 && (
                <p style={{textAlign: 'center', color: '#888', fontSize: '13px', padding: '20px'}}>Sin conversaciones aún.</p>
              )}
              {conversaciones.map((c) => (
                <div key={c._id} style={styles.convItem} onClick={async () => { await seleccionarConversacion(c._id); setView('chat'); }}>
                  <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1}}>{c.titulo}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); eliminarConversacion(c._id); }}
                    title="Eliminar"
                    style={{background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer', marginLeft: '8px'}}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
          <div style={styles.messagesArea}>
            {loadingConv && <p style={{textAlign: 'center', color: '#888', fontSize: '13px'}}>Cargando conversación…</p>}
            {!loadingConv && messages.length === 0 && (
              <div style={styles.msgBot}>¡Hola! Soy tu asistente de datos. Pregúntame sobre ventas, clientes, inventario o proveedores. También puedo generar gráficas. ¡Intenta!</div>
            )}
            {messages.map((msg, index) => (
              <div key={index}>
                <div style={msg.role === 'user' ? styles.msgUser : styles.msgBot}>
                  <TextoConFotos content={msg.text} esUsuario={msg.role === 'user'} />
                  {msg.chart && (
                      <div id={`chart-${index}`}>
                        <ChartRenderer config={msg.chart} enableClick={true} />
                      </div>
                  )}
                </div>

                {msg.role === 'assistant' && (
                  (msg.chart && msg.chart.data && msg.chart.data.length > 0) ||
                  (msg.rawData && msg.rawData.length > 0)
                ) && (
                  <div style={{
                    marginTop: '5px',
                    marginBottom: '10px',
                    display: 'flex',
                    gap: '8px',
                    paddingLeft: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={async () => {
                        const dataToExport = msg.chart || { data: msg.rawData };
                        const chartId = msg.chart ? `chart-${index}` : null;
                        await downloadPDF(msg.text, dataToExport, chartId, 'reporte');
                      }}
                      style={{
                        backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px',
                        padding: '5px 10px', fontSize: '12px', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', gap: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
                      title="Descargar como PDF"
                    >
                      📄 PDF
                    </button>
                    <button
                      onClick={() => {
                        const dataToExport = (msg.chart && msg.chart.data) || msg.rawData;
                        downloadExcel(dataToExport, 'datos');
                      }}
                      style={{
                        backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px',
                        padding: '5px 10px', fontSize: '12px', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', gap: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#218838'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
                      title="Descargar como Excel"
                    >
                      📊 Excel
                    </button>
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div style={{...styles.msgBot, fontStyle:'italic', color:'#666'}}>
                <span style={styles.typingDots}>
                  <span style={{...styles.typingDot, animationDelay: '-0.32s'}} />
                  <span style={{...styles.typingDot, animationDelay: '-0.16s'}} />
                  <span style={styles.typingDot} />
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          )}

          {view === 'chat' && (
          <form style={styles.inputArea} onSubmit={handleSend}>
            <textarea
              ref={inputRef}
              style={styles.input}
              value={input}
              onChange={handleInputChange}
              placeholder="Escribe tu pregunta..."
              disabled={isLoading}
              autoFocus
              rows={1}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
            />
            <button type="submit" disabled={isLoading} style={{cursor: 'pointer', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', padding: '8px 15px'}}>
                {isLoading ? '...' : 'Enviar'}
            </button>
          </form>
          )}
        </div>
      )}

      {!isOpen && (
        <button style={styles.button} onClick={() => setIsOpen(true)}>
          <Stars size={22} />
        </button>
      )}
    </div>
  );
};

export default ChatWidget;
